/**
 * Manages the screenshot protection overlay DOM element.
 * Visual appearance is fully controlled by the consumer-provided overlayHtml.
 */
export class ScreenshotOverlay {
    private overlay: HTMLElement | null = null;
    private customHtml: string;
    private dismissOnClick: boolean;

    constructor(customHtml: string, dismissOnClick = true) {
        this.customHtml = customHtml;
        this.dismissOnClick = dismissOnClick;
    }

    /**
     * Shows the screenshot protection overlay
     * Dismisses on user interaction (click or keyup)
     */
    show(): void {
        if (this.overlay) {
            return;
        }

        this.overlay = this.createOverlay();
        document.body.appendChild(this.overlay);

        if (this.dismissOnClick) {
            document.addEventListener('click', this.handleDismiss, { once: true });
        }

        document.addEventListener('keyup', this.handleKeyUp, { once: true });
    }

    /**
     * Hides the screenshot protection overlay
     */
    hide(): void {
        if (!this.overlay) {
            return;
        }

        if (this.dismissOnClick) {
            document.removeEventListener('click', this.handleDismiss);
        }

        document.removeEventListener('keyup', this.handleKeyUp);

        this.overlay.remove();
        this.overlay = null;
    }

    /**
     * Cleans up the overlay (called on destroy)
     */
    destroy(): void {
        this.hide();
    }

    private handleDismiss = (): void => {
        this.hide();
    };

    private handleKeyUp = (event: KeyboardEvent): void => {
        // Ignore keyup events that are part of a system screenshot shortcut.
        // e.g. Win+Shift+S fires keyup {key:'S', metaKey:true} back to the browser
        // after the OS handles it — we don't want that to dismiss the overlay.
        const isScreenshotShortcut = event.metaKey || (event.ctrlKey && event.shiftKey);

        if (isScreenshotShortcut) {
            // Re-register the listener so we still dismiss on the next real keyup
            document.addEventListener('keyup', this.handleKeyUp, { once: true });
            return;
        }

        this.hide();
    };

    private createOverlay(): HTMLElement {
        const overlay = document.createElement('div');
        overlay.className = 'copydeter-screenshot-overlay';
        overlay.innerHTML = this.customHtml;

        Object.assign(overlay.style, {
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            zIndex: '2147483647',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        });

        return overlay;
    }
}
